import { ConflictException, Injectable, NotFoundException } from '@nestjs/common';
import { ChatMessage, ChatSession, ChatSessionMode, ChatSessionStatus, Prisma, ServiceRequestStatus } from '@chat/prisma-client';
import { PrismaService } from '../prisma/prisma.service';
import { ChatResponder } from './chat-responder';
import { CreateSessionDto } from './dto/create-session.dto';
import { isFuzzyMatch } from './retrieval/fuzzy';
import { tokenize } from './retrieval/text';
import { ServiceRequestService } from './service-request.service';

// Shown once, the moment a session is escalated — not generated by the
// responder, since this isn't a reply to what the shopper said, it's a
// status-change confirmation. Kept as an "assistant" message (not "system")
// so it's actually visible in the widget's message list. Sets the expectation
// (a human, within 24 hours) and asks for details up front so the agent can
// answer in one round-trip instead of opening with "what's your order number?".
const ESCALATION_MESSAGE =
  "Got it — a team member will get back to you here within 24 hours. Meanwhile, please share the full details of your issue (order number, what happened, anything that helps) so we can give you a solution faster.";

// Shown when the shopper switches back to the bot mid-conversation — the
// mirror of ESCALATION_MESSAGE. Doesn't imply the ticket is gone (it isn't,
// see switchToBot()'s comment) — just that the bot is answering again.
const BOT_RETURN_MESSAGE =
  "You're back with our automated assistant — ask away, or tap \"Talk to a person\" anytime to reach the team again.";

const CLOSED_SESSION_MESSAGE = 'This conversation has ended — start a new one to keep chatting.';

// Attributed to the customer when their own action (not an agent's) causes
// a ServiceRequestEvent — e.g. reopening a stale, pre-migration SR by
// switching back to agent (see markEscalated's comment). Falls back to this
// when the session has no external identity (anonymous shopper).
const ANONYMOUS_ACTOR = { id: 'anonymous', name: 'Customer' };

// A shopper typing "I need to talk to an agent" has already chosen a human —
// answering with the FAQ fallback at that moment is the worst possible reply.
// Matched with the same tokenizer + fuzzy matcher the responder uses, so
// typos and plurals count. "talk"/"speak" are included because in a support
// chat they are themselves requests for a person ("can I speak to someone"),
// and they rescue heavily-typoed asks: a real session's "task to agne" is 2
// edits from "agent" (out of fuzzy budget) but 1 from "talk".
const HUMAN_INTENT_WORDS = ['agent', 'human', 'person', 'someone', 'representative', 'executive', 'staff', 'talk', 'speak'];

function wantsHuman(message: string): boolean {
  return tokenize(message).some((word) =>
    HUMAN_INTENT_WORDS.some((intent) => word === intent || isFuzzyMatch(word, intent)),
  );
}

@Injectable()
export class ChatService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly responder: ChatResponder,
    private readonly serviceRequests: ServiceRequestService,
  ) {}

  async createSession(applicationId: string, dto: CreateSessionDto): Promise<ChatSession> {
    return this.prisma.chatSession.create({
      data: {
        applicationId,
        externalUserId: dto.externalUserId,
        externalUserName: dto.externalUserName,
        externalUserEmail: dto.externalUserEmail,
      },
    });
  }

  async getSession(applicationId: string, sessionId: string): Promise<ChatSession> {
    return this.getSessionOrThrow(applicationId, sessionId);
  }

  // Shopper-facing ticket summary — just enough for the widget to show
  // "SR-10024 · In Progress" and know whose presence to check, never the
  // full agent-only ServiceRequest (notes, timeline, etc.). Returns null for
  // a pure-bot session that never escalated — there's no ticket yet.
  async findTicket(
    sessionId: string,
  ): Promise<{ srNumber: number; status: ServiceRequestStatus; assignedAgentId: string | null; assignedAgentName: string | null } | null> {
    return this.prisma.serviceRequest.findUnique({
      where: { sessionId },
      select: { srNumber: true, status: true, assignedAgentId: true, assignedAgentName: true },
    });
  }

  // Server-resolved "my active session," keyed by identity rather than a
  // client-cached id — the fix for a real bug: the widget used to trust
  // whatever session id was cached in the browser's localStorage, so a
  // shared/kiosk browser (or a different user logging in after someone
  // else, without clearing storage) could silently resume a stranger's
  // conversation. Most-recent by lastMessageAt regardless of status, so a
  // closed thread still resolves here (the widget shows the "ended" state
  // rather than losing track of it) instead of masking it with a fresh one.
  async findMySession(applicationId: string, externalUserId: string): Promise<ChatSession | null> {
    return this.prisma.chatSession.findFirst({
      where: { applicationId, externalUserId },
      orderBy: { lastMessageAt: 'desc' },
    });
  }

  // Idempotent — switching to agent while already in agent mode is a no-op
  // that returns the session as-is, so a double-click (or a widget that
  // reloaded and doesn't yet know it already switched) can't spam a second
  // confirmation message or notification. First-time escalation within this
  // session creates the Service Request wrapping it — throws (409) if the
  // customer already has a non-closed one elsewhere, per the "one active SR"
  // business rule; see markEscalated() for what happens on every subsequent
  // switch back to agent.
  async escalate(applicationId: string, sessionId: string): Promise<{ session: ChatSession; message: ChatMessage | null }> {
    const session = await this.getSessionOrThrow(applicationId, sessionId);
    this.assertOpen(session);

    if (session.mode === 'agent') {
      return { session, message: null };
    }

    const { session: updated, message } = await this.markEscalated(session);
    return { session: updated, message };
  }

  // The customer's own choice to stop talking to a human and go back to
  // instant bot answers — does not touch the Service Request at all (it
  // keeps existing/being worked by the agent in the background); this only
  // flips who the *next* message goes to. Idempotent, same reasoning as
  // escalate() above.
  async switchToBot(applicationId: string, sessionId: string): Promise<{ session: ChatSession; message: ChatMessage | null }> {
    const session = await this.getSessionOrThrow(applicationId, sessionId);
    this.assertOpen(session);

    if (session.mode === 'bot') {
      return { session, message: null };
    }

    const message = await this.prisma.chatMessage.create({
      data: { sessionId: session.id, role: 'assistant', content: BOT_RETURN_MESSAGE, metadata: { modeSwitch: true } },
    });

    const updated = await this.prisma.chatSession.update({
      where: { id: session.id },
      data: { mode: 'bot', lastMessageAt: new Date(), lastSeenAt: new Date() },
    });

    return { session: updated, message };
  }

  // Customer-initiated "end this conversation" — only for a pure-bot thread
  // that never escalated (no Service Request exists). Once a ticket exists,
  // closing must go through the agent workflow
  // (ServiceRequestService.updateStatus) so it isn't lost from the queue;
  // that path cascades back to this session's status too. Idempotent.
  async closeSession(applicationId: string, sessionId: string): Promise<ChatSession> {
    const session = await this.getSessionOrThrow(applicationId, sessionId);

    if (session.status === 'closed') {
      return session;
    }

    const existingServiceRequest = await this.prisma.serviceRequest.findUnique({ where: { sessionId: session.id } });
    if (existingServiceRequest) {
      throw new ConflictException('This conversation has an active support ticket — it can only be closed by an agent.');
    }

    return this.prisma.chatSession.update({
      where: { id: session.id },
      data: { status: 'closed', closedAt: new Date() },
    });
  }

  async listMessages(applicationId: string, sessionId: string): Promise<ChatMessage[]> {
    await this.getSessionOrThrow(applicationId, sessionId);

    // Fetching history implies the widget panel is open — cheap recency
    // signal for a future "recent chats" view, best-effort only.
    this.prisma.chatSession
      .update({ where: { id: sessionId }, data: { lastSeenAt: new Date() } })
      .catch(() => undefined);

    return this.prisma.chatMessage.findMany({
      where: { sessionId },
      orderBy: { createdOn: 'asc' },
    });
  }

  async sendMessage(
    applicationId: string,
    sessionId: string,
    content: string,
  ): Promise<{
    userMessage: ChatMessage;
    assistantMessage: ChatMessage | null;
    sessionStatus: ChatSessionStatus;
    sessionMode: ChatSessionMode;
  }> {
    const session = await this.getSessionOrThrow(applicationId, sessionId);
    this.assertOpen(session);

    const userMessage = await this.prisma.chatMessage.create({
      data: { sessionId, role: 'user', content },
    });

    // In agent mode a human owns the conversation — the bot answering over
    // them would bury the agent's replies and make "talk to a person"
    // meaningless. The shopper's message is stored and waits.
    if (session.mode === 'agent') {
      await this.prisma.chatSession.update({
        where: { id: sessionId },
        data: { lastMessageAt: new Date(), lastSeenAt: new Date() },
      });

      return { userMessage, assistantMessage: null, sessionStatus: session.status, sessionMode: 'agent' };
    }

    // Asking for a human IS choosing escalation — same effect as tapping
    // "Talk to a person", minus the shopper having to find the button.
    if (wantsHuman(content)) {
      const { message } = await this.markEscalated(session);
      return { userMessage, assistantMessage: message, sessionStatus: session.status, sessionMode: 'agent' };
    }

    const reply = await this.responder.respond(session, content);

    // links rides inside the same metadata JSON the widget already reads
    // suggestions/agentName from — there's no separate column for it. Only
    // built when there's actually something to merge, so a responder that
    // returns no metadata and no links still persists metadata as
    // undefined (not an empty object), matching today's behavior exactly.
    const metadata = reply.links?.length ? { ...(reply.metadata ?? {}), links: reply.links } : reply.metadata;

    const assistantMessage = await this.prisma.chatMessage.create({
      data: {
        sessionId,
        role: 'assistant',
        content: reply.content,
        metadata: metadata as Prisma.InputJsonValue | undefined,
      },
    });

    await this.prisma.chatSession.update({
      where: { id: sessionId },
      data: { lastMessageAt: new Date(), lastSeenAt: new Date(), responderKey: this.responder.key },
    });

    return { userMessage, assistantMessage, sessionStatus: session.status, sessionMode: 'bot' };
  }

  // Creates the session's one-and-only ServiceRequest on first escalation
  // (sessionId is unique on ServiceRequest, so there can never be a second
  // one for this session) and reuses it on every switch back to agent after
  // that — the SR keeps tracking the whole session's escalation history
  // rather than restarting, since bot/agent mode toggling is independent of
  // the ticket workflow. The one case a SR could already be 'closed' here
  // (reopen it rather than error) is pre-migration data: before mode/status
  // were split, closing a ticket never closed its session, so an old
  // session could be revisited by chat.service.spec's own historical data
  // with a closed SR still attached — not a path new data can reach, since
  // ServiceRequestService.updateStatus's close now cascades to the session
  // too (closed session ⇒ assertOpen already rejected the call above).
  private async markEscalated(session: ChatSession): Promise<{ session: ChatSession; message: ChatMessage }> {
    const existingServiceRequest = await this.prisma.serviceRequest.findUnique({ where: { sessionId: session.id } });

    if (!existingServiceRequest) {
      await this.serviceRequests.createForEscalation(session);
    } else if (existingServiceRequest.status === 'closed') {
      const actor = session.externalUserId
        ? { id: session.externalUserId, name: session.externalUserName ?? ANONYMOUS_ACTOR.name }
        : ANONYMOUS_ACTOR;
      await this.serviceRequests.reopen(session.applicationId, existingServiceRequest.id, actor);
    }

    const message = await this.prisma.chatMessage.create({
      data: { sessionId: session.id, role: 'assistant', content: ESCALATION_MESSAGE, metadata: { escalation: true } },
    });

    const updated = await this.prisma.chatSession.update({
      where: { id: session.id },
      data: { mode: 'agent', lastMessageAt: new Date(), lastSeenAt: new Date() },
    });

    return { session: updated, message };
  }

  private assertOpen(session: ChatSession): void {
    if (session.status === 'closed') {
      throw new ConflictException(CLOSED_SESSION_MESSAGE);
    }
  }

  // ── Agent-side operations (AgentJwtGuard-scoped) ─────────────────────────
  // applicationId: null means a platform-wide agent — no application filter.

  async listSessionsForAgent(applicationId: string | null): Promise<unknown[]> {
    return this.prisma.chatSession.findMany({
      where: applicationId ? { applicationId } : undefined,
      orderBy: { lastMessageAt: 'desc' },
      include: {
        application: { select: { id: true, slug: true, name: true } },
        // One query gives the inbox its preview line per session.
        messages: { orderBy: { createdOn: 'desc' }, take: 1 },
      },
    });
  }

  // Scope check without the payload — for lightweight endpoints (typing
  // heartbeats, live flags) that must enforce the same ownership rule as the
  // transcript itself.
  async assertAgentSession(applicationId: string | null, sessionId: string): Promise<void> {
    await this.getSessionForAgentOrThrow(applicationId, sessionId);
  }

  async listMessagesForAgent(applicationId: string | null, sessionId: string): Promise<ChatMessage[]> {
    await this.getSessionForAgentOrThrow(applicationId, sessionId);

    return this.prisma.chatMessage.findMany({
      where: { sessionId },
      orderBy: { createdOn: 'asc' },
    });
  }

  async agentReply(
    applicationId: string | null,
    sessionId: string,
    content: string,
    agentName: string,
  ): Promise<ChatMessage> {
    await this.getSessionForAgentOrThrow(applicationId, sessionId);

    const message = await this.prisma.chatMessage.create({
      // agentName is display attribution for the shopper ("Priya · Support"),
      // not an auth artifact — the JWT already proved who the agent is.
      data: { sessionId, role: 'agent', content, metadata: { agentName } },
    });

    await this.prisma.chatSession.update({
      where: { id: sessionId },
      data: { lastMessageAt: new Date() },
    });

    return message;
  }

  private async getSessionForAgentOrThrow(applicationId: string | null, sessionId: string): Promise<ChatSession> {
    const session = await this.prisma.chatSession.findUnique({ where: { id: sessionId } });

    if (!session || (applicationId !== null && session.applicationId !== applicationId)) {
      throw new NotFoundException('Chat session not found');
    }

    return session;
  }

  // Never distinguishes "doesn't exist" from "belongs to another
  // application" — a 404 either way, so one tenant can't probe for another
  // tenant's session ids.
  private async getSessionOrThrow(applicationId: string, sessionId: string): Promise<ChatSession> {
    const session = await this.prisma.chatSession.findUnique({ where: { id: sessionId } });

    if (!session || session.applicationId !== applicationId) {
      throw new NotFoundException('Chat session not found');
    }

    return session;
  }
}
